import { type ApiContact, type ApiItem, fetchCatalog } from "./api.ts";
import { Lightbox } from "./lightbox.ts";
import { availabilityLabel } from "./availability.ts";
import { renderPrice } from "./price.ts";

function requiredElement<T extends Element>(
    id: string,
    expectedType: { new (): T },
): T {
    const element = document.getElementById(id);

    if (!(element instanceof expectedType)) {
        throw new Error(`Required element "#${id}" is missing`);
    }

    return element;
}

function createCard(
    item: ApiItem,
    priceFormatter: Intl.NumberFormat,
    imageIndex: number,
    openItem: (
        item: ApiItem,
        opener: HTMLButtonElement,
    ) => void,
): HTMLLIElement {
    const cover = item.images[item.coverIndex];

    if (!cover) {
        throw new Error(`Item "${item.id}" has no cover image`);
    }

    const listItem = document.createElement("li");
    listItem.className = "gallery-item";

    const article = document.createElement("article");
    article.className = "item-card";

    const figure = document.createElement("figure");
    figure.className = "item-card__figure";

    const image = document.createElement("img");
    image.className = "item-card__image";
    image.src = cover.thumbnail.url;
    image.alt = item.name;
    image.width = cover.thumbnail.width;
    image.height = cover.thumbnail.height;
    image.loading = imageIndex < 2 ? "eager" : "lazy";
    image.decoding = "async";
    image.sizes = "(max-width: 600px) calc(100vw - 2rem), " +
        "(max-width: 1000px) 50vw, 33vw";

    if (imageIndex === 0) {
        image.setAttribute("fetchpriority", "high");
    }

    const availability = document.createElement("span");
    availability.className = "item-card__availability";
    availability.classList.add(
        item.availability === "in_stock"
            ? "item-card__availability--in-stock"
            : "item-card__availability--made-to-order",
    );
    availability.textContent = availabilityLabel(
        item.availability,
    );
    figure.append(image, availability);

    const caption = document.createElement("div");
    caption.className = "item-card__caption";

    const headingRow = document.createElement("div");
    headingRow.className = "item-card__heading";

    const title = document.createElement("h2");
    title.className = "item-card__title";
    title.textContent = item.name;

    const price = document.createElement("p");
    price.className = "item-card__price";

    renderPrice(price, item, priceFormatter);
    headingRow.append(title, price);

    const description = document.createElement("p");
    description.className = "item-card__description";
    description.textContent = item.description;

    const openButton = document.createElement("button");
    openButton.type = "button";
    openButton.className = "item-card__open";
    openButton.setAttribute(
        "aria-label",
        `View photos of ${item.name}, ${availabilityLabel(item.availability)}`,
    );
    openButton.setAttribute("aria-haspopup", "dialog");
    openButton.addEventListener("click", () => {
        openItem(item, openButton);
    });

    caption.append(headingRow, description);
    article.append(figure, caption, openButton);
    listItem.append(article);

    return listItem;
}

function renderContacts(
    contacts: ApiContact[],
    section: HTMLElement,
    list: HTMLUListElement,
): void {
    list.replaceChildren();

    if (contacts.length === 0) {
        section.hidden = true;
        return;
    }

    section.hidden = false;

    const fragment = document.createDocumentFragment();

    for (const contact of contacts) {
        const item = document.createElement("li");
        const link = document.createElement("a");
        const label = document.createElement("span");
        const externalIndicator = document.createElement("span");

        link.className = "contact__link";
        link.href = contact.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";

        label.textContent = contact.label;

        externalIndicator.textContent = "↗";
        externalIndicator.setAttribute("aria-hidden", "true");

        link.append(label, externalIndicator);
        item.append(link);
        fragment.append(item);
    }

    list.append(fragment);
}

async function initialize(): Promise<void> {
    const title = requiredElement(
        "site-title",
        HTMLHeadingElement,
    );
    const tagline = requiredElement(
        "site-tagline",
        HTMLParagraphElement,
    );
    const status = requiredElement(
        "gallery-status",
        HTMLParagraphElement,
    );
    const grid = requiredElement(
        "item-grid",
        HTMLUListElement,
    );

    const contactSection = requiredElement(
        "contact-section",
        HTMLElement,
    );
    const contactLinks = requiredElement(
        "contact-links",
        HTMLUListElement,
    );

    try {
        const catalog = await fetchCatalog();

        title.textContent = catalog.site.title;
        tagline.textContent = catalog.site.tagline;
        renderContacts(
            catalog.site.contacts,
            contactSection,
            contactLinks,
        );
        document.title = `${catalog.site.title} — Leathercraft`;

        if (catalog.items.length === 0) {
            status.textContent = "New work will be added soon.";
            return;
        }

        const priceFormatter = new Intl.NumberFormat(
            undefined,
            {
                style: "currency",
                currency: catalog.site.currency,
            },
        );
        const lightbox = new Lightbox(priceFormatter);
        const fragment = document.createDocumentFragment();

        catalog.items.forEach((item, index) => {
            fragment.append(
                createCard(
                    item,
                    priceFormatter,
                    index,
                    (selectedItem, opener) => {
                        lightbox.open(selectedItem, opener);
                    },
                ),
            );
        });

        grid.replaceChildren(fragment);
        status.textContent = `${catalog.items.length} handcrafted ${
            catalog.items.length === 1 ? "piece" : "pieces"
        }`;
    } catch (error) {
        console.error(error);
        status.classList.add("gallery-status--error");
        status.textContent =
            "The gallery could not be loaded. Please try again later.";
    }
}

void initialize();
